/**
 * Pure helper for table copy. Extracted from MarkdownRenderer's
 * EnhancedTable so the contract can be unit-tested without JSDOM.
 *
 * #674 — the previous `handleCopyTable` called
 * `navigator.clipboard.writeText` with no error handling. When the
 * call rejected (insecure context, permission denied, no clipboard
 * API support) the user got zero feedback — the icon never swapped,
 * the page showed no toast, and the clipboard was empty. This helper
 * normalizes the result into a discriminated union the caller can
 * branch on for both success-toast and error-toast paths.
 */

export type CopyTableResult =
  | { kind: 'ok' }
  | { kind: 'unsupported' }
  | { kind: 'error'; reason: string };

/**
 * Copy `text` to the user's clipboard. Returns a tagged result so the
 * caller can render a toast or accessible status message rather than
 * silently failing.
 *
 * - `ok` — text was written
 * - `unsupported` — clipboard API not available (insecure HTTP, old
 *   browser, headless context). Caller may fall back to a `<textarea>`
 *   + `execCommand('copy')` shim if they choose, but for security
 *   reasons this helper does NOT mutate the DOM.
 * - `error` — call rejected (permission denied, document not focused,
 *   transient failure). `reason` carries the underlying message for
 *   the toast.
 */
export async function copyTextToClipboard(
  text: string,
  // Allow the caller to inject a clipboard implementation for tests.
  // Defaults to the real navigator.clipboard when available.
  clipboard?: Pick<Clipboard, 'writeText'> | null,
): Promise<CopyTableResult> {
  const cb =
    clipboard !== undefined
      ? clipboard
      : typeof navigator !== 'undefined'
        ? navigator.clipboard
        : null;

  if (!cb || typeof cb.writeText !== 'function') {
    return { kind: 'unsupported' };
  }

  try {
    await cb.writeText(text);
    return { kind: 'ok' };
  } catch (err) {
    return {
      kind: 'error',
      reason: err instanceof Error ? err.message : 'Copy failed',
    };
  }
}

/**
 * Map a `CopyTableResult` to a user-facing message suitable for a
 * toast or aria-live region. Kept pure so it round-trips in tests.
 */
export function copyResultToMessage(result: CopyTableResult): {
  tone: 'success' | 'error';
  text: string;
} {
  switch (result.kind) {
    case 'ok':
      return { tone: 'success', text: 'Table copied to clipboard' };
    case 'unsupported':
      return {
        tone: 'error',
        text: 'Copy is unavailable in this browser context.',
      };
    case 'error':
      return { tone: 'error', text: `Copy failed: ${result.reason}` };
  }
}
